import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createRxnormMedication } from "../../../services/medicationService";
import { MEDICATION_ROUTES } from "./medication.constants";

const fieldClass =
  "block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100";
const labelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";

const TTY_OPTIONS = ["CUSTOM", "SCD", "SBD", "GPCK", "BPCK"];
const DOSE_FORM_OPTIONS = [
  "Tablet",
  "Capsule",
  "Solution",
  "Suspension",
  "Cream",
  "Gel",
  "Ointment",
  "Lotion",
  "Patch",
  "Spray",
  "Syrup",
  "Injection",
  "Powder",
  "Packet",
  "Inhaler",
  "Other",
];

/**
 * Modal to add a missing medication into the shared RxNorm catalog.
 * Required: medicationId, medicationName, tty, dosage, route, doseForm, brandName, genericName.
 */
const AddMedicationModal = ({
  isOpen,
  onClose,
  onCreated,
  initialName = "",
}) => {
  const [medicationId, setMedicationId] = useState("");
  const [medicationName, setMedicationName] = useState("");
  const [tty, setTty] = useState("CUSTOM");
  const [dosage, setDosage] = useState("");
  const [route, setRoute] = useState(MEDICATION_ROUTES[0]);
  const [doseForm, setDoseForm] = useState("Tablet");
  const [brandName, setBrandName] = useState("");
  const [genericName, setGenericName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const seed = String(initialName || "").trim();
    setMedicationId("");
    setMedicationName(seed);
    setTty("CUSTOM");
    setDosage("");
    setRoute(MEDICATION_ROUTES[0]);
    setDoseForm("Tablet");
    setBrandName("");
    setGenericName(seed);
    setErrors({});
    setSaving(false);
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const validate = () => {
    const next = {};
    if (!String(medicationId).trim()) next.medicationId = "Medication ID is required";
    if (!String(medicationName).trim()) {
      next.medicationName = "Medication name is required";
    }
    if (!String(tty).trim()) next.tty = "TTY is required";
    if (!String(dosage).trim()) next.dosage = "Dosage is required";
    if (!String(route).trim()) next.route = "Route is required";
    if (!String(doseForm).trim()) next.doseForm = "Dose form is required";
    if (!String(brandName).trim()) next.brandName = "Brand name is required";
    if (!String(genericName).trim()) {
      next.genericName = "Generic name is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const res = await createRxnormMedication({
        medicationId: medicationId.trim(),
        medicationName: medicationName.trim(),
        tty,
        dosage: dosage.trim(),
        route,
        doseForm: doseForm.trim(),
        brandName: brandName.trim(),
        genericName: genericName.trim(),
      });
      const payload = res.result || res;
      const medication = payload.medication || payload;
      toast.success("Medication added to catalog");
      onCreated?.(medication);
      onClose?.();
    } catch (err) {
      const message =
        err?.message ||
        err?.error?.message ||
        "Failed to add medication. Please try again.";
      toast.error(message);
      if (/already exists/i.test(message)) {
        setErrors((prev) => ({ ...prev, medicationId: message }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-medication-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-indigo-100 bg-indigo-50/70 sticky top-0">
          <h2
            id="add-medication-title"
            className="text-sm font-semibold text-indigo-900 uppercase tracking-wide"
          >
            Add Medication
          </h2>
          <p className="mt-0.5 text-[11px] text-slate-600">
            Add a new medication to the shared catalog for all users.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelClass}>
                Medication ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={fieldClass}
                value={medicationId}
                maxLength={20}
                placeholder="e.g. LOCAL001"
                autoFocus
                onChange={(e) => setMedicationId(e.target.value)}
              />
              {errors.medicationId ? (
                <p className="text-[11px] text-red-600">{errors.medicationId}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                TTY <span className="text-red-500">*</span>
              </label>
              <select
                className={fieldClass}
                value={tty}
                onChange={(e) => setTty(e.target.value)}
              >
                {TTY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {errors.tty ? (
                <p className="text-[11px] text-red-600">{errors.tty}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-1">
            <label className={labelClass}>
              Medication Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={fieldClass}
              value={medicationName}
              maxLength={1000}
              placeholder="e.g. Custom Compound Capsule 10 MG"
              onChange={(e) => setMedicationName(e.target.value)}
            />
            {errors.medicationName ? (
              <p className="text-[11px] text-red-600">{errors.medicationName}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelClass}>
                Dosage <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={fieldClass}
                value={dosage}
                maxLength={100}
                placeholder="e.g. 10 MG"
                onChange={(e) => setDosage(e.target.value)}
              />
              {errors.dosage ? (
                <p className="text-[11px] text-red-600">{errors.dosage}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Route <span className="text-red-500">*</span>
              </label>
              <select
                className={fieldClass}
                value={route}
                onChange={(e) => setRoute(e.target.value)}
              >
                {MEDICATION_ROUTES.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {errors.route ? (
                <p className="text-[11px] text-red-600">{errors.route}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-1">
            <label className={labelClass}>
              Dose Form <span className="text-red-500">*</span>
            </label>
            <select
              className={fieldClass}
              value={doseForm}
              onChange={(e) => setDoseForm(e.target.value)}
            >
              {DOSE_FORM_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors.doseForm ? (
              <p className="text-[11px] text-red-600">{errors.doseForm}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelClass}>
                Brand Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={fieldClass}
                value={brandName}
                maxLength={255}
                placeholder="Brand name"
                onChange={(e) => setBrandName(e.target.value)}
              />
              {errors.brandName ? (
                <p className="text-[11px] text-red-600">{errors.brandName}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Generic Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={fieldClass}
                value={genericName}
                maxLength={255}
                placeholder="Generic name"
                onChange={(e) => setGenericName(e.target.value)}
              />
              {errors.genericName ? (
                <p className="text-[11px] text-red-600">{errors.genericName}</p>
              ) : null}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="h-9 px-3 text-sm font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              Save Medication
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMedicationModal;
